import { IconName } from '../components/ui/Icon';
import {
  supportCallDisplay,
  supportCallNumber,
  supportWhatsappDisplay,
  supportWhatsappUrl,
} from '../services/support';

/**
 * Seed data for the account's own pages - cards, reviews and the help desk.
 *
 * ---------------------------------------------------------------------------
 * NONE OF THIS IS REAL
 * ---------------------------------------------------------------------------
 * There is no payments provider, no reviews store and no help desk. Every list
 * below is a fixed array so the screens can be built and judged; each one is
 * what a returning account would see.
 *
 * Two things to know before wiring a backend to these:
 *
 *   - No card number is stored, only the last four and the brand. That is all
 *     a list needs, and it is the only part a client is ever allowed to hold.
 *   - `when` is pre-formatted rather than a timestamp. Nothing here does
 *     relative-time arithmetic, and a real feed sends the string it wants
 *     shown anyway.
 */

// ------------------------------------------------------------------ payment

export type PaymentKind = 'upi' | 'card' | 'wallet';

export type PaymentMethod = {
  id: string;
  kind: PaymentKind;
  /** "UPI", "Visa", "Wallet Balance" - the line you read first. */
  label: string;
  /**
   * The handle, the masked number, or the balance - already formatted.
   *
   * This is the display form of the typed fields below. Both exist because the
   * account list only ever prints it, while checkout has to compare a wallet
   * balance against a total and cannot do that with "₹450.00". Change one and
   * change the other.
   */
  detail: string;
  /** Expiry for a card; nothing for the others. */
  note?: string;
  isDefault: boolean;

  // ---------------------------------------------- typed, for checkout to use
  /** Cards: the last four. Never the rest - a client may not hold it. */
  last4?: string;
  /** Cards: MM/YY, as printed. */
  expires?: string;
  /** UPI: the saved handle, prefilled at the gateway. */
  vpa?: string;
  /** Wallet: whole rupees on hand. */
  balance?: number;
};

export const paymentIcon: Record<PaymentKind, IconName> = {
  upi: 'bolt',
  card: 'creditCard',
  wallet: 'wallet',
};

/**
 * Saved instruments. EMPTY, and it has to stay empty until there is a vault.
 *
 * This held five invented instruments - a UPI id "rohan@okhdfcbank", three
 * cards ending 4242 / 1234 / 5678, and a "Wallet Balance ₹450.00" - and they
 * were not confined to a demo screen. `data/payment.ts` derives `savedVpa` and
 * `savedCards` from this array, so the CHECKOUT offered a stranger's UPI id
 * and three cards nobody owns as things to pay with, and the account page
 * listed them as though they were on file.
 *
 * Nothing here is stored anywhere. There is no tokenisation, no vault and no
 * gateway customer - Razorpay collects the instrument itself at checkout (see
 * `components/payment/RazorpayCheckout.tsx`), which is why the app has nothing
 * to list. An empty array is the truthful answer, and every consumer already
 * handles it: the checkout hides "Saved Cards" when `savedCards` is empty and
 * the account page draws an empty state.
 *
 * When a vault exists, this becomes a fetch, not a longer literal.
 */
export const paymentMethods: PaymentMethod[] = [];

// ------------------------------------------------------------------ reviews

/*
 * Reviews used to live here.
 *
 * They moved to `src/data/reviews.ts` when the composer was built: a review
 * grew a comment, tags, photographs and an edit window, and a second copy of
 * the shape in this file would have drifted from it inside a week. The account
 * screens read them from there now.
 */

// ------------------------------------------------------------------ support

export type HelpTopic = {
  id: string;
  question: string;
  /** The answer, shown when the row is opened. */
  answer: string;
  icon: IconName;
};

export const helpTopics: HelpTopic[] = [
  {
    id: 'h1',
    question: 'My booking is delayed',
    answer:
      'Open the booking and use Chat to message the professional directly - they can see how far away they are. If nobody replies within 10 minutes, cancel free of charge and we will find someone else.',
    icon: 'users',
  },
  {
    id: 'h2',
    question: 'I want to cancel my booking',
    answer:
      'Open the booking and choose Cancel. It is free until a professional is assigned; after that a small fee applies, and the exact amount is shown before you confirm.',
    icon: 'clock',
  },
  {
    id: 'h3',
    question: 'I didn’t receive OTP',
    answer:
      'Wait for the timer to finish and tap Resend. If it still does not arrive, check that the number is right and that SMS is not blocked - you can also sign in on another number and change it later.',
    icon: 'lock',
  },
  {
    id: 'h4',
    question: 'Payment failed but amount deducted',
    answer:
      'Nothing is lost. A failed payment is reversed by your bank automatically, usually within 3 to 5 working days. The booking is not confirmed, so you can pay again straight away.',
    icon: 'creditCard',
  },
];

export type SupportChannel = {
  id: string;
  label: string;
  detail: string;
  icon: IconName;
  /** The word on the control, when it has one. */
  action?: string;
  /** `tel:`, `mailto:` or an https URL. Opened with `Linking`. */
  url?: string;
};

/**
 * Where support email goes.
 *
 * Written once rather than at both the row's label and its `mailto:` - those
 * two had drifted apart before in this file's phone numbers, and an address
 * that is DISPLAYED correctly while the tap opens a different one is the worst
 * version of this bug, because it passes a read-through.
 */
const SUPPORT_EMAIL = 'support@itshomingo.com';

/**
 * The two headline channels, shown as a pair of cards.
 *
 * ---------------------------------------------------------------------------
 * FUNCTIONS, BECAUSE THE NUMBER IS NOT KNOWN AT IMPORT TIME
 * ---------------------------------------------------------------------------
 * These were arrays, and they carried `tel:+911234567890` and
 * `wa.me/911234567890` - the mockup's placeholders, live in the app since it
 * was written. Anybody who tapped Call Now on this page rang a stranger.
 *
 * The real number comes from ops settings, which arrive after this module is
 * imported, so an array evaluated at module scope would freeze whatever the
 * fallback happened to be at launch. Read per render instead - the resolvers
 * in `services/support.ts` own the number, and nothing here writes one down.
 */
export const primaryChannels = (): SupportChannel[] => [
  {
    id: 'c1',
    label: 'Chat with us',
    detail: 'We’re online',
    action: 'Start Chat',
    icon: 'chat',
  },
  {
    id: 'c2',
    label: 'Call us',
    detail: supportCallDisplay(),
    action: 'Call Now',
    icon: 'phone',
    url: `tel:+${supportCallNumber()}`,
  },
];

/** Everything else, as rows. */
export const otherChannels = (): SupportChannel[] => [
  {
    id: 'c3',
    label: 'WhatsApp Support',
    detail: supportWhatsappDisplay(),
    icon: 'whatsapp',
    url: supportWhatsappUrl('Hi Homingo, I need some help.'),
  },
  {
    id: 'c4',
    label: 'Email Support',
    /*
     * `itshomingo.com`, which is the domain that exists.
     *
     * This read `support@homingo.com` - and `homingo.com` does not resolve at
     * all; the Pro app's `config/links.ts` says so in writing, having hit the
     * same thing. So every message a customer sent from this row bounced, and
     * the bounce went to them rather than to us: from our side it looked like
     * nobody ever emailed support.
     */
    detail: SUPPORT_EMAIL,
    icon: 'share',
    url: `mailto:${SUPPORT_EMAIL}`,
  },
];

/** Naive contains-match over the questions, standing in for a help search. */
export const searchHelp = (query: string): HelpTopic[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return helpTopics;
  }
  return helpTopics.filter(
    topic =>
      topic.question.toLowerCase().includes(needle) ||
      topic.answer.toLowerCase().includes(needle),
  );
};
